import React, { useMemo } from 'react'
import { useFragment, graphql } from 'react-relay'

import { useLocation } from 'react-router-dom'

import { YStack, XStack, Separator } from 'tamagui'
import {
  Strong,
  Paragraph,
  UserDefaultPhoto,
  UserPrimaryPhoto,
  Card,
  FormattedMarkdown,
  H2,
  Link,
  Button,
  Icon,
} from '@newComponents'

import { EventScreenPresentations_event$key } from './__generated__/EventScreenPresentations_event.graphql'

export const EventScreenPresentations = ({
  event,
}: {
  event: EventScreenPresentations_event$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventScreenPresentations_event on Event {
        prettyUrl

        owner {
          ... on User {
            isMe
          }

          ... on Guild {
            myMembership {
              membershipLevel
            }
          }
        }

        presentations {
          edges {
            node {
              rowId
              prettyUrl

              title
              description

              presenterFirstName
              presenterLastName

              # Signed URL for guest presenter avatar (when no linked User)
              guestPresenterAvatarUrl(
                transform: { w: 48, h: 48, dpr: 3, fo: "auto" }
              )

              presenter {
                firstName
                lastName

                ...UserPrimaryPhotoNew_user
              }
            }

            cursor
          }
        }
      }
    `,
    event
  )

  const location = useLocation()

  const canCreate = useMemo(() => {
    return (
      data.owner?.isMe ||
      ['ORGANIZER', 'EDITOR'].includes(
        data.owner?.myMembership?.membershipLevel || ''
      )
    )
  }, [data])

  const createButton = canCreate ? (
    <Link
      href={`/events/${data.prettyUrl}/settings/presentations/new`}
      options={{ state: { backgroundLocation: location } }}
      hoverStyle={{ textDecorationLine: 'none' }}
    >
      <Button
        errorFlash={false}
        margin={-6}
        small
        secondary
        paddingHorizontal='$2'
        gap='$1'
      >
        <Icon name='add-filled' size='$3' />

        <Button.Text size='$4' secondary>
          Create
        </Button.Text>
      </Button>
    </Link>
  ) : null

  if (!data.presentations.edges.length) {
    if (canCreate) {
      return (
        <Card gap='$5'>
          <XStack>
            <Paragraph size='$4' color='$colorMuted' fontWeight='$5' flex={1}>
              Presentations
            </Paragraph>

            {createButton}
          </XStack>

          <YStack gap='$4' alignItems='center'>
            <Paragraph
              textAlign='center'
              size='$4'
              fontWeight='$6'
              color='$colorMuted'
              maxWidth={400}
            >
              Presentations are the individual talks, workshops, or other
              content for this event.
            </Paragraph>

            <YStack gap='$1'>
              <Paragraph
                textAlign='center'
                size='$4'
                fontWeight='$6'
                color='$colorMuted'
                maxWidth={400}
              >
                You can Create a Presentation with the button above.
              </Paragraph>

              <Paragraph
                textAlign='center'
                size='$4'
                fontWeight='$6'
                color='$colorMuted'
                maxWidth={400}
              >
                Give it a Title, Description, and the Presenter.
              </Paragraph>

              <Paragraph
                textAlign='center'
                size='$4'
                fontWeight='$6'
                color='$colorMuted'
                maxWidth={400}
              >
                Presenters can then Edit the information on their Presentations.
              </Paragraph>

              <Paragraph
                textAlign='center'
                size='$4'
                fontWeight='$6'
                color='$colorMuted'
                maxWidth={400}
              >
                After the event, you can add a link to a video recording or
                their slides on to the same Presentation so everything's in one
                place.
              </Paragraph>
            </YStack>

            <Paragraph
              textAlign='center'
              size='$4'
              fontWeight='$6'
              color='$colorMuted'
              maxWidth={400}
            >
              Presentations are displayed on Event Listings, Emails, your
              Guild's Profile, as well as the Presenter's profile.
            </Paragraph>
          </YStack>
        </Card>
      )
    }

    return null
  }

  return (
    <Card gap='$5'>
      <XStack>
        <Paragraph size='$4' color='$colorMuted' fontWeight='$5' flex={1}>
          Presentations
        </Paragraph>

        {createButton}
      </XStack>

      <YStack gap='$6'>
        {data.presentations.edges.map((edge, index) => {
          return (
            <Link
              key={edge.cursor}
              href={`/presentations/${edge.node.prettyUrl}`}
              options={{
                state: {
                  backgroundLocation:
                    location.state?.backgroundLocation || location,
                },
              }}
              underlineHover={false}
            >
              <YStack gap='$6'>
                {!!index && <Separator borderColor='$borderColor' />}

                <XStack gap='$5'>
                  <YStack gap='$2' alignItems='center' width={20 * 4}>
                    {edge.node.presenter ? (
                      <>
                        <UserPrimaryPhoto
                          user={edge.node.presenter}
                          size='$12'
                        />

                        <Paragraph size='$4' textAlign='center'>
                          {edge.node.presenter.firstName}{' '}
                          {edge.node.presenter.lastName}
                        </Paragraph>
                      </>
                    ) : (
                      <>
                        <UserDefaultPhoto
                          defaultAvatarUrl={edge.node.guestPresenterAvatarUrl}
                          width={12 * 4}
                          height={12 * 4}
                          borderRadius={12 * 4}
                        />

                        <Paragraph size='$4' textAlign='center'>
                          {edge.node.presenterFirstName}{' '}
                          {edge.node.presenterLastName}
                        </Paragraph>
                      </>
                    )}
                  </YStack>

                  <YStack gap='$2' flex={1}>
                    <H2 fontWeight='$6'>{edge.node.title}</H2>

                    <FormattedMarkdown
                      textSize='$4'
                      formattedVariant='PLAIN_TEXT'
                    >
                      {edge.node.description}
                    </FormattedMarkdown>
                  </YStack>
                </XStack>
              </YStack>
            </Link>
          )
        })}
      </YStack>
    </Card>
  )
}
